'use client';

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { analytics } from '@/src/components/features/property/lib';
import type { ContactSubmissionContext } from '@/src/components/features/property/ContactForm/types';
import { useAfterPaint } from '@/src/lib/hooks/ui/useAfterPaint';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';

const ContactFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ContactForm/ContactFormOverlay').then((mod) => mod.ContactFormOverlay),
);

const PostContactFlowModal = dynamic(() =>
  import('@/src/components/features/property/ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

function createRentalApplicationId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const getZillowApplicationUrl = (
  aliasEncoded: string,
  sessionParams?: { medium: 'web' | 'mweb'; rentalApplicationId: string },
) => {
  const url = new URL(
    `https://www.zillow.com/renter-hub/applications/listing/${encodeURIComponent(aliasEncoded)}/rental-application/`,
  );
  // Zillow attributes application purchases to HotPads by these, as legacy web sent them.
  url.searchParams.set('utm_source', 'hotpads');
  url.searchParams.set('utm_campaign', 'applyNow');
  if (sessionParams) {
    url.searchParams.set('utm_medium', sessionParams.medium);
    url.searchParams.set('rentalApplicationId', sessionParams.rentalApplicationId);
  }
  return url.toString();
};

export type ApplyLink = {
  href: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

export const useApplyAction = (listing?: PropertyDetailListing | null, triggerLocation = 'home_details_halo') => {
  const t = useTranslations('contactForm');
  const [isOpen, setIsOpen] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const aliasEncoded =
    listing?.rentalApplicationStatus?.toLowerCase() === 'accepted' ? listing.aliasEncoded || null : null;

  const isDesktop = useIsDesktop();
  // Created after hydration, so the server and client render the same href.
  const [rentalApplicationId, setRentalApplicationId] = useState<string | null>(null);
  useEffect(() => setRentalApplicationId(createRentalApplicationId()), []);
  const href = aliasEncoded
    ? getZillowApplicationUrl(
        aliasEncoded,
        rentalApplicationId ? { medium: isDesktop ? 'web' : 'mweb', rentalApplicationId } : undefined,
      )
    : null;

  const { run: openAfterPaint } = useAfterPaint(() => setIsOpen(true));

  const openApply = useCallback(() => {
    void openAfterPaint();
  }, [openAfterPaint]);

  const handleApplyLinkClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!href) return;
      if (!(event.currentTarget instanceof HTMLAnchorElement)) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      analytics.emitTrackRentalAppPreStartEvent({ triggerLocation, listing });
      // A later application from this page gets its own id, after the browser has read this href.
      setTimeout(() => setRentalApplicationId(createRentalApplicationId()));
    },
    [href, listing, triggerLocation],
  );

  const handleSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setIsOpen(false);
    setPostContactContext(context);
  }, []);

  const applyLink: ApplyLink | null = href ? { href, onClick: handleApplyLinkClick } : null;

  const applyOverlay =
    isOpen || postContactContext ? (
      <>
        {isOpen && (
          <ContactFormOverlay
            open={isOpen}
            onOpenChange={setIsOpen}
            title={t('titleApply')}
            listing={listing}
            mode="requestToApply"
            onSubmitSuccess={handleSubmitSuccess}
          />
        )}
        <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
      </>
    ) : null;

  return { applyLink, openApply, applyOverlay };
};
